import { fetchConfig } from '@theme/utilities';
import { scheduleSinkAddonPricePreview } from '@theme/sink-add-on';

/**
 * @param {Element | null} section
 * @returns {HTMLFormElement | null}
 */
function findProductForm(section) {
  if (!section) return null;
  const form = section.querySelector('form[action*="/cart/add"]');
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 * @param {string} value
 */
function upsertFormProperty(form, name, value) {
  const fieldName = `properties[${name}]`;
  const existing = form.querySelector(`input[name="${CSS.escape(fieldName)}"]`);
  const input =
    existing instanceof HTMLInputElement
      ? existing
      : (() => {
          const created = document.createElement('input');
          created.type = 'hidden';
          created.name = fieldName;
          form.appendChild(created);
          return created;
        })();
  input.value = value;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} name
 */
function removeFormProperty(form, name) {
  const fieldName = `properties[${name}]`;
  form.querySelectorAll(`input[name="${CSS.escape(fieldName)}"]`).forEach((el) => el.remove());
}

/**
 * @param {Element} picker
 * @returns {{ cartPropertyKey: string, centerSlot: number }}
 */
function readPickerConfig(picker) {
  try {
    const el = picker.querySelector('script[type="application/json"][data-placement-picker="config"]');
    const parsed = el?.textContent?.trim() ? JSON.parse(el.textContent) : null;
    const rawSlot = parsed?.centerSlot;
    const centerSlot = typeof rawSlot === 'number' && rawSlot > 0 ? rawSlot : 2;
    return { cartPropertyKey: parsed?.cartPropertyKey || 'Placering vask', centerSlot };
  } catch {
    return { cartPropertyKey: 'Placering vask', centerSlot: 2 };
  }
}

/** @type {Map<string, Promise<Record<string, unknown> | null>>} */
const placementProductCache = new Map();

/**
 * @param {string} handle
 * @returns {Promise<Record<string, unknown> | null>}
 */
async function fetchPlacementProductJson(handle) {
  const h = String(handle || '').trim();
  if (!h) return null;
  const cached = placementProductCache.get(h);
  if (cached) return cached;
  const pending = fetch(`/products/${encodeURIComponent(h)}.js`, {
    headers: { Accept: 'application/json' },
  })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  placementProductCache.set(h, pending);
  return pending;
}

/**
 * @param {string} handle
 * @returns {Promise<number | null>}
 */
async function resolveFirstAvailableVariantId(handle) {
  const json = await fetchPlacementProductJson(handle);
  if (!json || !Array.isArray(json.variants)) return null;
  for (const v of json.variants) {
    if (!v || typeof v !== 'object') continue;
    const variant = /** @type {Record<string, unknown>} */ (v);
    if (variant.available === false) continue;
    const id = typeof variant.id === 'number' ? variant.id : parseInt(String(variant.id), 10);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

class SinkPlacementPicker extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Always sit immediately after sink-variant-picker in the DOM, regardless of block_order.
    // Moving the element triggers disconnect + reconnect; the second call finds the correct
    // position and skips this branch.
    const parent = this.parentElement;
    const sinkVariantPicker = parent?.querySelector('sink-variant-picker');
    if (
      sinkVariantPicker instanceof Element &&
      sinkVariantPicker.parentElement === parent &&
      sinkVariantPicker.nextElementSibling !== this
    ) {
      sinkVariantPicker.after(this);
      return;
    }

    const section = this.closest('.shopify-section');

    // Show immediately if a sink is already checked in the DOM (handles page load with non-default sink).
    const sinkPicker = section?.querySelector('sink-variant-picker');
    if (sinkPicker?.querySelector('input[data-sink-option-type="vask"]:checked')) {
      this.#show();
    }

    // Respond to sink selection changes for the lifetime of this element.
    section?.addEventListener(
      'sink-picker:change',
      (event) => {
        if (!(event instanceof CustomEvent)) return;
        if (event.detail?.noSink) {
          this.#hide();
        } else {
          this.#show();
        }
      },
      { signal }
    );

    // Respond to drawer count changes, including drawers auto-selected on width change.
    section?.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.closest('.custom-variant-picker')) return;
        this.#applyDrawerConstraints(this.#getDrawerCount(section));
      },
      { signal }
    );

    const fieldset = this.querySelector('[data-placement-options-fieldset]');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    /**
     * @param {EventTarget | null} target
     * @returns {HTMLInputElement | null}
     */
    const resolvePlacementRadioFromTarget = (target) => {
      if (!(target instanceof Element)) return null;
      const label = target.closest('label.placement-option-button');
      if (!label) return null;
      const input = label.querySelector('input[type="radio"].placement-option-button__input');
      return input instanceof HTMLInputElement ? input : null;
    };

    /**
     * Block deselecting an already-selected placement option.
     * @param {Event} event
     */
    const blockPlacementDeselect = (event) => {
      const input = resolvePlacementRadioFromTarget(event.target);
      if (!input || !input.checked) return;
      event.preventDefault();
    };

    const captureOpts = { capture: true, signal };
    fieldset.addEventListener('pointerdown', blockPlacementDeselect, captureOpts);
    fieldset.addEventListener('mousedown', blockPlacementDeselect, captureOpts);
    fieldset.addEventListener('touchstart', blockPlacementDeselect, { ...captureOpts, passive: false });

    /**
     * @param {HTMLInputElement} input
     */
    const selectAndApply = (input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.checked) input.checked = true;
      this.#applyFromInput(input);
    };

    fieldset.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
        selectAndApply(target);
      },
      { signal }
    );

    fieldset.addEventListener(
      'click',
      (event) => {
        const input = resolvePlacementRadioFromTarget(event.target);
        if (!input) return;
        selectAndApply(input);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * Show the picker and ensure a placement is selected and synced to the form.
   */
  #show() {
    this.removeAttribute('hidden');

    const section = this.closest('.shopify-section');
    this.#applyDrawerConstraints(this.#getDrawerCount(section));

    const fieldset = this.querySelector('[data-placement-options-fieldset]');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const checked = /** @type {HTMLInputElement | null} */ (
      fieldset.querySelector('input[data-placement-option-type="placement"]:checked')
    );

    if (checked) {
      // Re-sync the form property — it may have been cleared when the picker was hidden.
      this.#applyFromInput(checked);
    } else {
      // Auto-select the first visible placement option.
      const first = /** @type {HTMLInputElement | null} */ (
        fieldset.querySelector('label[data-placement-option-button]:not([hidden]) input[data-placement-option-type="placement"]')
      );
      if (first) {
        first.checked = true;
        this.#applyFromInput(first);
      }
    }
  }

  /**
   * Hide the picker and clear the placement form property.
   */
  #hide() {
    this.setAttribute('hidden', '');
    this.dataset.noPlacement = 'true';
    this.dataset.placementValue = '';
    this.dataset.extraCostCents = '';
    this.dataset.extraCostHandle = '';
    this.#clearFormProperty();
  }

  /**
   * @param {HTMLInputElement} input
   */
  #applyFromInput(input) {
    const value = (input.dataset.placementValue || input.value || '').trim();
    const extraCostCents = parseInt(input.dataset.placementExtraCostCents || '0', 10);
    const extraCostHandle = (input.dataset.placementExtraCostHandle || '').trim();

    this.dataset.noPlacement = 'false';
    this.dataset.placementValue = value;
    this.dataset.extraCostCents = extraCostCents > 0 ? String(extraCostCents) : '';
    this.dataset.extraCostHandle = extraCostHandle;

    this.#syncFormProperty(value);
    this.#updateUi(input);

    this.dispatchEvent(
      new CustomEvent('placement-picker:change', {
        bubbles: true,
        detail: {
          value,
          extraCostCents: extraCostCents || 0,
          extraCostHandle: extraCostHandle || null,
          noPlacement: false,
        },
      })
    );

    scheduleSinkAddonPricePreview(this.closest('.shopify-section'));
  }

  /**
   * @param {Element | null} section
   * @returns {number | null}
   */
  #getDrawerCount(section) {
    const checked = section?.querySelector('input[data-drawers-count]:checked');
    if (!(checked instanceof HTMLInputElement)) return null;
    const n = parseInt(checked.dataset.drawersCount || '', 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Show or hide placement options based on drawer count.
   * - 4 drawers: center slot hidden
   * - 2 drawers: only center slot visible
   * @param {number | null} drawerCount
   */
  #applyDrawerConstraints(drawerCount) {
    const { centerSlot } = readPickerConfig(this);
    const fieldset = this.querySelector('[data-placement-options-fieldset]');
    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    fieldset.querySelectorAll('label[data-placement-option-button]').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      const input = label.querySelector('input[data-placement-option-type="placement"]');
      if (!(input instanceof HTMLInputElement)) return;

      const slotMatch = (input.dataset.placementOptionId || '').match(/placement-(\d+)/);
      const slotNum = slotMatch ? parseInt(slotMatch[1], 10) : null;
      const isCenter = slotNum === centerSlot;

      const shouldHide =
        (drawerCount === 4 && isCenter) ||
        (drawerCount === 2 && !isCenter);

      label.toggleAttribute('hidden', shouldHide);
    });

    // If the currently checked option is now hidden, auto-select first visible.
    const checked = /** @type {HTMLInputElement | null} */ (
      fieldset.querySelector('input[data-placement-option-type="placement"]:checked')
    );
    if (checked) {
      const checkedLabel = checked.closest('label[data-placement-option-button]');
      if (checkedLabel instanceof HTMLElement && checkedLabel.hasAttribute('hidden')) {
        checked.checked = false;
        const firstVisible = /** @type {HTMLInputElement | null} */ (
          fieldset.querySelector('label[data-placement-option-button]:not([hidden]) input[data-placement-option-type="placement"]')
        );
        if (firstVisible) {
          firstVisible.checked = true;
          this.#applyFromInput(firstVisible);
        }
      }
    }
  }

  /**
   * @param {string} value
   */
  #syncFormProperty(value) {
    const section = this.closest('.shopify-section');
    const form = findProductForm(section);
    if (!form) return;
    const { cartPropertyKey } = readPickerConfig(this);
    if (value) {
      upsertFormProperty(form, cartPropertyKey, value);
    } else {
      removeFormProperty(form, cartPropertyKey);
    }
  }

  #clearFormProperty() {
    const section = this.closest('.shopify-section');
    const form = findProductForm(section);
    if (!form) return;
    const { cartPropertyKey } = readPickerConfig(this);
    removeFormProperty(form, cartPropertyKey);
  }

  /**
   * @param {HTMLInputElement | null} selectedInput
   */
  #updateUi(selectedInput) {
    const labelEl = selectedInput?.closest('.placement-option-button');
    const title =
      labelEl?.getAttribute('title')?.trim() ||
      selectedInput?.getAttribute('aria-label') ||
      labelEl?.querySelector('.placement-option-button__text-label')?.textContent?.trim() ||
      '';
    this.#updateSelectedLabel(title);
    this.#updateButtonStates(selectedInput);
  }

  /**
   * @param {string} text
   */
  #updateSelectedLabel(text) {
    const el = this.querySelector('[data-placement-selected-label]');
    if (el instanceof HTMLElement) el.textContent = text;
  }

  /**
   * @param {HTMLInputElement | null} selectedInput
   */
  #updateButtonStates(selectedInput) {
    this.querySelectorAll('.placement-option-button').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      const input = label.querySelector('.placement-option-button__input');
      const isSelected = input instanceof HTMLInputElement && input === selectedInput;
      label.classList.toggle('placement-option-button--selected', isSelected);
    });
  }
}

if (!customElements.get('sink-placement-picker')) {
  customElements.define('sink-placement-picker', SinkPlacementPicker);
}

/**
 * Adds the surcharge product to the cart when a placement with an extra cost is selected.
 * Called from product-form.js after the main product add succeeds.
 *
 * @param {Element | null} section
 * @param {string[]} sectionIds
 * @returns {Promise<{ sections?: Record<string, string> } | null>}
 */
export async function tryAddPlacementAddonAfterMainAdd(section, sectionIds) {
  if (!section) return null;
  const picker = section.querySelector('sink-placement-picker');
  if (!(picker instanceof HTMLElement)) return null;

  const handle = (picker.dataset.extraCostHandle || '').trim();
  if (!handle) return null;

  const variantId = await resolveFirstAvailableVariantId(handle);
  if (!variantId) return null;

  const cartUrl =
    typeof Theme !== 'undefined' && /** @type {any} */ (Theme).routes?.cart_add_url
      ? /** @type {any} */ (Theme).routes.cart_add_url
      : '';
  if (!cartUrl) return null;

  const body = {
    items: [
      {
        id: variantId,
        quantity: 1,
        properties: {
          _placement_addon: '1',
          Placering: picker.dataset.placementValue || '',
        },
      },
    ],
    sections: sectionIds.join(','),
    sections_url: typeof window !== 'undefined' ? window.location.pathname : '/',
  };

  const response = await fetch(cartUrl, {
    ...fetchConfig('javascript'),
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await response.text().catch(() => '');
    return null;
  }

  return response.json().catch(() => null);
}

export { SinkPlacementPicker };
